import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Clock, CreditCard } from 'lucide-react';
import { useClinicProfile } from '../../context/ClinicProfileContext';
import type { TenantHomePageContent } from '../../types/site-pages';
import { buildTenantPath } from '../../utils/tenantSlug';

interface CTASectionProps {
  mode?: 'global' | 'tenant';
  tenantContent?: TenantHomePageContent | null;
}

const CTASection = ({ mode = 'global', tenantContent }: CTASectionProps) => {
  const isTenant = mode === 'tenant';
  const { effectiveTenantSlug } = useClinicProfile();

  if (isTenant) {
    const cta = tenantContent?.cta;
    const title = cta?.title || 'Comece sua jornada de recuperação hoje mesmo';
    const body =
      cta?.body ||
      'Nossa equipe especializada está pronta para criar um plano de tratamento personalizado para suas necessidades.';
    const primaryLabel = cta?.primaryCta?.label || 'Agendar Consulta';
    const primaryPath = cta?.primaryCta?.path || '/agendar';
    const secondaryLabel = cta?.secondaryCta?.label || 'Fale Conosco';
    const secondaryPath = cta?.secondaryCta?.path || '/contato';

    return (
      <section className="py-20 bg-primary-700 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-primary-600/20" />
          <div className="absolute top-full -right-24 -translate-y-1/2 w-80 h-80 rounded-full bg-primary-800/30" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-display font-bold text-white mb-6"
            >
              {title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg text-neutral-100 mb-8"
            >
              {body}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to={buildTenantPath(primaryPath, effectiveTenantSlug)}
                className="bg-white hover:bg-neutral-100 text-primary-700 font-medium py-3 px-8 rounded-md transition-colors"
              >
                {primaryLabel}
              </Link>
              <Link
                to={buildTenantPath(secondaryPath, effectiveTenantSlug)}
                className="border border-white hover:bg-white/10 text-white font-medium py-3 px-8 rounded-md transition-colors"
              >
                {secondaryLabel}
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  /* ─── GLOBAL MODE: CTA final de conversão ─── */
  return (
    <section className="py-20 md:py-28 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Background decorativo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-cyan-500/10 blur-[150px]" />
        <div className="absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-violet-500/10 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 leading-tight"
          >
            Pronto para transformar a gestão da sua clínica?
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed"
          >
            Junte-se a mais de 50 clínicas que já automatizaram sua operação com
            o RevaHub. Comece hoje mesmo — sem compromisso.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-10"
          >
            <Link
              to="/cadastro"
              className="group inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-4 px-8 rounded-lg transition-all duration-200 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-400/30 text-lg"
            >
              Comece grátis por 14 dias
              <ArrowRight
                size={20}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:bg-white/5 text-white font-medium py-4 px-8 rounded-lg transition-all duration-200 text-lg"
            >
              Agendar demonstração
            </Link>
          </motion.div>

          {/* Trust signals */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500"
          >
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-slate-500" />
              <span>Sem cartão de crédito</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-slate-500" />
              <span>Setup em menos de 5 minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-slate-500" />
              <span>Cancele quando quiser</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
